import { ParentTypes } from '@risksmart-app/domain/src/types/consts';
import i18n from '@risksmart-app/i18n/src/i18n';
import type { GetFormConfigurationQuery } from '@risksmart-app/web-graphql-client/generated/graphql';

import type {
  RelationalEntityGroup,
  WidgetDataSource,
} from '../../gigawidget/types';

type FilterOptions = {
  /** When true, categories marked with isHiddenWhenAggregationsEnabled are excluded */
  isAggregationsEnabled?: boolean;
};

// Singular `*_one` taxonomy key → plural `*_other` key. Used to derive the
// entity group heading in attribute selectors (RSP-5299).
const groupHeadingKey: Record<
  RelationalEntityGroup,
  | 'action_other'
  | 'control_other'
  | 'impact_rating_other'
  | 'indicator_other'
  | 'issue_other'
  | 'obligation_other'
  | 'risk_other'
> = {
  action_one: 'action_other',
  control_one: 'control_other',
  impact_rating_one: 'impact_rating_other',
  indicator_one: 'indicator_other',
  issue_one: 'issue_other',
  obligation_one: 'obligation_other',
  risk_one: 'risk_other',
};

const resolveEntityHeading = (group: RelationalEntityGroup): string =>
  i18n.format(i18n.t(groupHeadingKey[group]), 'titleCase');

/**
 * Resolves a `form_field_configuration.FieldId` to the org's label override.
 * `entityGroup` scopes the lookup to one form; omit it for a single-form source.
 */
export type StandardFieldLabelResolver = (
  fieldId: string,
  entityGroup?: RelationalEntityGroup
) => string | undefined;

type FormConfigurationRow =
  GetFormConfigurationQuery['form_configuration'][number];

/**
 * The two fields the label lookup reads, with their types derived from the
 * generated query result rather than restated. A handwritten shape would keep
 * compiling if the fragment stopped selecting `Label`, and the resolver would
 * then find no overrides at all — the same silent fallback this file exists to
 * prevent. Narrowed via `Pick` so test fixtures need only these two fields.
 */
type FormConfigurationForLabels = {
  ParentType?: FormConfigurationRow['ParentType'] | null;
  fields_config?:
    | Pick<FormConfigurationRow['fields_config'][number], 'FieldId' | 'Label'>[]
    | null;
};

// `RelationalEntityGroup` is the singular taxonomy key (`risk_one`); a
// `form_configuration.ParentType` is the bare entity (`risk`). Sourced from the
// domain constants rather than written out: a renamed ParentType would
// otherwise stop matching silently, and every relational getter would fall back
// to its i18n name with no test failing.
export const entityGroupParentType: Record<RelationalEntityGroup, string> = {
  action_one: ParentTypes.Action,
  control_one: ParentTypes.Control,
  impact_rating_one: ParentTypes.ImpactRating,
  indicator_one: ParentTypes.Indicator,
  issue_one: ParentTypes.Issue,
  obligation_one: ParentTypes.Obligation,
  risk_one: ParentTypes.Risk,
};

/**
 * Builds the label lookup for the widget's category/sub-category selectors, so
 * a standard field a customer has renamed on its form is findable there by the
 * name they actually use (CUS-480).
 *
 * Only non-empty overrides are indexed; everything else falls back to the
 * category getter's i18n name.
 *
 * Overrides are indexed per form as well as flat. A relational getter carries an
 * `entityGroup`, which names its form and makes the lookup exact — `Status` on
 * the Risk form is not `Status` on the Action form. A getter without one is
 * resolved from the flat index, where a FieldId that two of the fetched forms
 * relabel differently stays unresolvable rather than being guessed at.
 */
export const createStandardFieldLabelResolver = (
  formConfigurations: FormConfigurationForLabels[] | null | undefined
): StandardFieldLabelResolver => {
  const labels = new Map<string, string>();
  const ambiguous = new Set<string>();
  const byParentType = new Map<string, Map<string, string>>();

  formConfigurations?.forEach((formConfiguration) => {
    const parentType = formConfiguration.ParentType ?? undefined;
    formConfiguration.fields_config?.forEach(({ FieldId, Label }) => {
      const label = Label?.trim();
      if (!label) {
        return;
      }

      if (parentType) {
        const forParent =
          byParentType.get(parentType) ?? new Map<string, string>();
        forParent.set(FieldId, label);
        byParentType.set(parentType, forParent);
      }

      const existing = labels.get(FieldId);
      if (existing !== undefined && existing !== label) {
        ambiguous.add(FieldId);

        return;
      }
      labels.set(FieldId, label);
    });
  });

  return (fieldId, entityGroup) => {
    if (entityGroup) {
      return byParentType.get(entityGroupParentType[entityGroup])?.get(fieldId);
    }

    return ambiguous.has(fieldId) ? undefined : labels.get(fieldId);
  };
};

/**
 * The label a category should display: the org's override when this getter
 * declares the FieldId it reads, otherwise its i18n name.
 */
const resolveCategoryLabel = (
  categoryGetter: {
    entityGroup?: RelationalEntityGroup;
    fieldId?: string;
    name: () => string;
  },
  resolveStandardFieldLabel?: StandardFieldLabelResolver
): string =>
  (categoryGetter.fieldId
    ? resolveStandardFieldLabel?.(
        categoryGetter.fieldId,
        categoryGetter.entityGroup
      )
    : undefined) ?? categoryGetter.name();

export const getStandardFieldOptions = <TDataSource extends WidgetDataSource>(
  categoryGetters: TDataSource['categoryGetters'],
  filterOptions?: FilterOptions,
  resolveStandardFieldLabel?: StandardFieldLabelResolver
) => {
  const standardFields = categoryGetters
    .filter((cg) => !cg.date)
    .filter(
      (cg) =>
        !filterOptions?.isAggregationsEnabled ||
        !cg.isHiddenWhenAggregationsEnabled
    );

  // RSP-5299: relational data source categories declare an `entityGroup`,
  // which we translate into per-entity headings preserving source order.
  // Single sources have no entityGroup and fall back to one "Standard Fields"
  // group, matching the pre-existing behaviour.
  const hasEntityGroups = standardFields.some((cg) => cg.entityGroup);
  if (hasEntityGroups) {
    const groups: {
      label: string;
      options: { label: string; value: string }[];
    }[] = [];
    for (const cg of standardFields) {
      const entityGroup = cg.entityGroup;
      if (!entityGroup) {
        continue;
      }
      const heading = resolveEntityHeading(entityGroup);
      let group = groups.find((g) => g.label === heading);
      if (!group) {
        group = { label: heading, options: [] };
        groups.push(group);
      }
      group.options.push({
        label: resolveCategoryLabel(cg, resolveStandardFieldLabel),
        value: cg.id,
      });
    }

    return groups;
  }

  return [
    {
      label: 'Standard Fields',
      options:
        standardFields.map((categoryGetter) => ({
          label: resolveCategoryLabel(
            categoryGetter,
            resolveStandardFieldLabel
          ),
          value: categoryGetter.id,
        })) ?? [],
    },
  ];
};

export const getDateFieldOptions = <TDataSource extends WidgetDataSource>(
  categoryGetters: TDataSource['categoryGetters'],
  showDateFields?: boolean,
  filterOptions?: FilterOptions,
  resolveStandardFieldLabel?: StandardFieldLabelResolver
) => {
  return !showDateFields
    ? []
    : [
        {
          label: 'Date Fields',
          options:
            categoryGetters
              .filter((cg) => cg.date)
              .filter(
                (cg) =>
                  !filterOptions?.isAggregationsEnabled ||
                  !cg.isHiddenWhenAggregationsEnabled
              )
              .map((categoryGetter) => ({
                label: resolveCategoryLabel(
                  categoryGetter,
                  resolveStandardFieldLabel
                ),
                value: categoryGetter.id,
              })) ?? [],
        },
      ];
};

export const getCustomAttributeOptions = (
  customAttributeOptions?: { label: string; value: string }[]
) => {
  if (!customAttributeOptions || customAttributeOptions.length < 1) {
    return [];
  }

  return [
    {
      label: 'Custom Fields',
      options: customAttributeOptions,
    },
  ];
};
